/**
 * CENTRALIZED DOMICILIATION PERMISSIONS HOOK
 * 
 * This is the main hook that components use to check domiciliation permissions.
 * It integrates with Redux and the centralized ACL module.
 * 
 * Usage:
 * - With token: const { canView, isLoading } = useDomiciliationPermission(specialToken);
 * - Without token (cliente): const { canView, isLoading } = useDomiciliationPermission();
 */

import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { fetchDomiciliationPermission } from '../redux/actions/domiciliationPermissionsActions';
import get from 'lodash/get';

/**
 * Hook to check domiciliation permissions
 * Compatible with react-redux 5.x (no hooks)
 * 
 * @param {string|null} specialToken - Token for call-center/anonymously/agente (optional)
 * @param {Function} dispatch - Redux dispatch (from connect)
 * @param {Object} permissionState - Permission state from Redux (from connect)
 * @returns {{canView: boolean, isLoading: boolean, error: string|null, userType: string|null, refetch: Function}}
 */
function useDomiciliationPermissionBase(specialToken = null, dispatch, permissionState) {
  const [localLoading, setLocalLoading] = useState(false);
  
  const {
    canView = false,
    isLoading: reduxLoading = false,
    error = null,
    userType = null,
    hasChecked = false
  } = permissionState || {};

  /**
   * Fetch permission from backend
   */
  const fetchPermission = async () => {
    setLocalLoading(true);
    try {
      await dispatch(fetchDomiciliationPermission(specialToken));
    } catch (err) {
      console.error('[useDomiciliationPermission] Error fetching permission:', err);
    } finally {
      setLocalLoading(false);
    }
  };

  /**
   * Refetch permission manually
   */
  const refetch = () => {
    fetchPermission();
  };

  // Fetch permission on mount or when token changes
  useEffect(() => {
    if (!hasChecked || specialToken) {
      fetchPermission();
    }
    // eslint-disable-next-line
  }, [specialToken]);

  return {
    canView,
    isLoading: reduxLoading || localLoading,
    error,
    userType,
    refetch
  };
}

/**
 * HOC to inject Redux state and dispatch
 * This makes it work with react-redux 5.x
 */
const mapStateToProps = (state) => ({
  permissionState: get(state, 'domiciliationPermissions', {})
});

// Create the connected hook
const ConnectedHook = connect(mapStateToProps)(
  ({ specialToken, permissionState, dispatch }) => {
    return useDomiciliationPermissionBase(specialToken, dispatch, permissionState);
  }
);

/**
 * Main hook export - works with react-redux 5.x
 */
export function useDomiciliationPermission(specialToken = null) {
  // For compatibility with react-redux 5.x, we need to use this differently
  // This is a workaround since we can't use useSelector/useDispatch
  const [state, setState] = useState({
    canView: false,
    isLoading: true,
    error: null,
    userType: null
  });

  useEffect(() => {
    // Import the direct check function
    import('../acl/domiciliation').then(({ checkDomiciliationPermission }) => {
      checkDomiciliationPermission(specialToken)
        .then(result => {
          setState({
            canView: result.canView,
            isLoading: false,
            error: null,
            userType: result.userType
          });
        })
        .catch(err => {
          setState({
            canView: false,
            isLoading: false,
            error: err.message,
            userType: null
          });
        });
    });
  }, [specialToken]);

  return state;
}




// WEBPACK FOOTER //
// ./src/hooks/useDomiciliationPermission.js